import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "email", "consent", "message", "submit"]
  static values  = { slug: String, url: { type: String, default: "/catalogues" } }

  declare readonly hasModalTarget: boolean
  declare readonly modalTarget: HTMLElement
  declare readonly hasEmailTarget: boolean
  declare readonly emailTarget: HTMLInputElement
  declare readonly hasConsentTarget: boolean
  declare readonly consentTarget: HTMLInputElement
  declare readonly hasMessageTarget: boolean
  declare readonly messageTarget: HTMLElement
  declare readonly hasSubmitTarget: boolean
  declare readonly submitTarget: HTMLButtonElement
  declare readonly slugValue: string
  declare readonly urlValue: string

  private _trigger: Element | null = null
  private _sending = false
  private _onKeydown!: (e: KeyboardEvent) => void

  connect() {
    this._trigger = null
    this._sending = false
    this._onKeydown = this._handleKeydown.bind(this)
    // Compatibilité : galerie_controller#ouvrirCatalogue appelle encore le global
    window.ouvrirModalCatalogue = () => this.open()
    window.fermerModalCatalogue = () => this.close()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    if (window.ouvrirModalCatalogue) delete window.ouvrirModalCatalogue
    if (window.fermerModalCatalogue) delete window.fermerModalCatalogue
  }

  open(event?: Event) {
    if (event) event.preventDefault()
    if (!this.hasModalTarget) return
    this._trigger = document.activeElement
    if (document.pointerLockElement) document.exitPointerLock()
    this.modalTarget.classList.add("is-open")
    document.addEventListener("keydown", this._onKeydown)
    const first = this.modalTarget.querySelector<HTMLElement>("input, button")
    if (first) first.focus()
  }

  close(event?: Event) {
    if (event) event.preventDefault()
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("is-open")
    document.removeEventListener("keydown", this._onKeydown)
    if (this._trigger instanceof HTMLElement) this._trigger.focus()
    this._trigger = null
  }

  async submit(event?: Event) {
    if (event) event.preventDefault()
    if (this._sending) return
    const email   = this.hasEmailTarget   ? this.emailTarget.value.trim() : ""
    const consent = this.hasConsentTarget ? this.consentTarget.checked   : false

    if (!email) return this._message("Indiquez votre adresse email.", "error")
    if (!consent) return this._message("Cochez la case pour recevoir le catalogue par email.", "error")

    this._sending = true
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ email, slug: this.slugValue })
      })
      const data: { success?: boolean, message?: string } = await response.json().catch(() => ({}))
      if (data.success) {
        this._message(data.message ?? "", "success")
        setTimeout(() => this.close(), 3000)
      } else {
        this._message(data.message || "Envoi impossible pour le moment. Réessayez dans quelques minutes.", "error")
      }
    } catch {
      this._message("Envoi impossible. Vérifiez votre connexion puis réessayez.", "error")
    } finally {
      this._sending = false
      if (this.hasSubmitTarget) this.submitTarget.disabled = false
    }
  }

  _message(text: string, kind: "error" | "success") {
    if (!this.hasMessageTarget) return
    const el = this.messageTarget
    el.hidden = false
    el.classList.toggle("is-error",   kind === "error")
    el.classList.toggle("is-success", kind === "success")
    el.textContent = text
  }

  _handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") { this.close(); return }
    if (e.key !== "Tab") return
    const focusable = Array.from(this.modalTarget.querySelectorAll<HTMLElement>("input:not([disabled]), button:not([disabled])"))
    if (!focusable.length) return
    const first = focusable[0], last = focusable[focusable.length - 1]
    if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
      e.preventDefault()
      ;(e.shiftKey ? last : first).focus()
    }
  }
}
